'use client';

import { useMemo } from 'react';

import { UnitPrice } from '@ravago/shared/page-data/models/elements';
import { QuotationLine } from '@ravago/shared/page-data/models/features/product-quotation.model';
import { Avatar } from '@ravago/shared/radiance/components/avatar/components/Avatar/Avatar';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';

import { useShoppingCartContext } from '../../../../context/shopping-cart-context';
import { usePathLocale } from '../../../../hooks/use-path-locale';
import { DynamicPageLink } from '../../dynamic-page-link/dynamic-page-link';
import { ProductPriceCardItem } from '../models/product-price-card-item.model';

import { ProductPriceCardQuotationLine } from './product-price-card-quotation-line';

export const ONLINE_DISCOUNT_PERCENTAGE = 15;

const Header = ({ slug, description, productDetailBaseRoute }: { slug: string | undefined; description: string; productDetailBaseRoute: string }) => {
   const locale = usePathLocale();

   if (slug) {
      return (
         <DynamicPageLink id={slug} baseRoute={{ url: productDetailBaseRoute, noFollow: true }} locale={locale} slug={slug}>
            <Typography variant="body-highlight" color="inherit">
               {description}
            </Typography>
         </DynamicPageLink>
      );
   }

   return (
      <Typography variant="body-highlight" color="subtle">
         {description}
      </Typography>
   );
};

const CardItem = ({ label, value, highlight = false }: { label: string; value: string | undefined; highlight?: boolean }) => (
   <GridCol spanDesktop={6} spanTablet={6} spanPhone={12}>
      <Box direction="column" gap="none">
         <Typography variant="small" color="subtle">
            {label}
         </Typography>
         <Typography variant={highlight ? 'body-highlight' : 'small'} color={highlight ? 'primary' : 'inherit'}>
            {value ?? '-'}
         </Typography>
      </Box>
   </GridCol>
);

const PriceSection = ({
   lowestUnitPrice,
   lowestOfflineUnitPrice,
   showOfflinePrices,
   showLimitedMyPrices,
   canViewPrices,
   expanded,
   toggleExpanded
}: {
   lowestUnitPrice: UnitPrice | undefined;
   lowestOfflineUnitPrice: UnitPrice | undefined;
   showOfflinePrices: boolean;
   showLimitedMyPrices: boolean;
   canViewPrices: boolean;
   expanded: boolean;
   toggleExpanded: () => void;
}) => {
   const translate = useTranslations();
   const format = useFormatter();

   if (!lowestUnitPrice) {
      if (!showLimitedMyPrices) {
         return (
            <Typography variant="small" color="subtle">
               {translate('prices.no-quote')}
            </Typography>
         );
      }
      return null;
   }

   if (!canViewPrices) {
      return <Typography>{translate('permissions.access-to-price-restricted-message')}</Typography>;
   }

   return (
      <>
         <BreakpointSwitch excludeViews={['phone']}>
            {showOfflinePrices && lowestOfflineUnitPrice && (
               <Box justify="end">
                  <Badge variant="primary">{translate('prices.online-discount', { percentage: ONLINE_DISCOUNT_PERCENTAGE })}</Badge>
               </Box>
            )}
         </BreakpointSwitch>

         <Box direction="column" gap="xs">
            <Button variant="secondary" trailingIcon={expanded ? 'chevron-up' : 'chevron-down'} onClick={toggleExpanded}>
               {translate('prices.starting-from')} {format.unitPrice(lowestUnitPrice)}
            </Button>
            {showOfflinePrices && lowestOfflineUnitPrice && (
               <Box justify="end">
                  <Typography variant="small" color="primary" lineThrough>
                     {format.unitPrice(lowestOfflineUnitPrice)}
                  </Typography>
               </Box>
            )}
         </Box>
      </>
   );
};

export const ProductPriceCard = ({
   productPriceCardItem,
   productPriceCardItemId,
   productDetailBaseRoute,
   quoteDetailBaseRoute,
   tdsAnchor,
   channel,
   isOverview,
   showShoppingCartBtn,
   hasOrderingPermission,
   showOfflinePrices,
   showRequestQuoteOption,
   showLimitedMyPrices,
   canViewPrices,
   onRequestQuote
}: {
   productPriceCardItem: ProductPriceCardItem;
   productPriceCardItemId: string;
   productDetailBaseRoute: string;
   quoteDetailBaseRoute: string | null;
   tdsAnchor: string | undefined;
   channel: string;
   isOverview?: boolean;
   showShoppingCartBtn: boolean;
   hasOrderingPermission: boolean;
   showOfflinePrices: boolean;
   showRequestQuoteOption: boolean;
   showLimitedMyPrices: boolean;
   canViewPrices: boolean;
   onRequestQuote: () => void;
}) => {
   const locale = usePathLocale();
   const translate = useTranslations();
   const format = useFormatter();
   const cartContext = useShoppingCartContext();

   const [expandedIds, setExpandedIds] = useQueryState('expandedIds', parseAsArrayOf(parseAsString).withDefault([]));

   const expanded = expandedIds.includes(productPriceCardItemId);

   const toggleExpanded = () => {
      setExpandedIds((curr) => {
         if (!curr.includes(productPriceCardItemId)) {
            return [...curr, productPriceCardItemId];
         }
         return curr.filter((productId) => productId !== productPriceCardItemId);
      });
   };

   const slug = useMemo(() => {
      if (!productPriceCardItem.slug) return undefined;

      if (productPriceCardItem.quotationLines && productPriceCardItem.quotationLines.length > 0) {
         const searchParams = new URLSearchParams();
         searchParams.set('from', 'quote');
         if (productPriceCardItem.colorId != null) searchParams.set('colorId', String(productPriceCardItem.colorId));
         if (productPriceCardItem.producerColorCode) searchParams.set('colorCode', productPriceCardItem.producerColorCode);
         return `${productPriceCardItem.slug}?${searchParams.toString()}`;
      }

      return productPriceCardItem.slug;
   }, [productPriceCardItem]);

   const handleAddToCart = (quotationLine: QuotationLine) => {
      if (!productPriceCardItem.packaging) return;
      cartContext.addToCart(
         { type: 'QUOTATION', id: quotationLine.id },
         {
            id: productPriceCardItem.productId,
            description: productPriceCardItem.description
         },
         productPriceCardItem.packaging,
         quotationLine.quantity
      );
   };

   return (
      <Paper>
         <Box direction="row" justify="between" gap="md">
            <BreakpointSwitch includeViews={['desktop', 'tab-land']}>
               <BoxItem>
                  <Avatar shape="square" src={productPriceCardItem.logoUrl} />
               </BoxItem>
            </BreakpointSwitch>
            <BoxItem grow={1} basis={0}>
               <Box direction="column" gap="md">
                  <Box direction="column" gap="xs">
                     {canViewPrices && showOfflinePrices && productPriceCardItem.lowestOfflineUnitPrice && (
                        <BreakpointSwitch includeViews={['phone']}>
                           <Box>
                              <Badge variant="primary">{translate('prices.online-discount', { percentage: ONLINE_DISCOUNT_PERCENTAGE })}</Badge>
                           </Box>
                        </BreakpointSwitch>
                     )}
                     <Header slug={slug} description={productPriceCardItem.description} productDetailBaseRoute={productDetailBaseRoute} />
                  </Box>
                  <Grid>
                     <CardItem
                        label={translate('product.card.product-ref')}
                        value={
                           productPriceCardItem.customerReferences && productPriceCardItem.customerReferences.length > 0
                              ? productPriceCardItem.customerReferences.join(' • ')
                              : undefined
                        }
                     />
                     <CardItem
                        label={translate('product.card.customer-ref', { customer: channel ?? '' })}
                        value={productPriceCardItem.businessContextReference?.toString()}
                     />
                     <CardItem
                        label={translate('product.card.last-ordered')}
                        value={productPriceCardItem.lastOrderedOn ? format.dateTime(productPriceCardItem.lastOrderedOn) : undefined}
                     />
                     <CardItem label={translate('product.card.polymer-type')} value={productPriceCardItem.polymerType} />
                  </Grid>
                  {productPriceCardItem.slug && (
                     <Box gap="none">
                        <DynamicPageLink
                           id={productPriceCardItem.slug}
                           baseRoute={{ url: productDetailBaseRoute, noFollow: true }}
                           locale={locale}
                           slug={slug}
                           anchor={tdsAnchor}
                        >
                           <Icon icon="file-alt" color="inherit" />
                           {translate('prices.related-documents')}
                        </DynamicPageLink>
                     </Box>
                  )}
                  <BreakpointSwitch includeViews={['phone']}>
                     {isOverview ? (
                        <>
                           <PriceSection
                              lowestUnitPrice={productPriceCardItem.lowestUnitPrice}
                              lowestOfflineUnitPrice={productPriceCardItem.lowestOfflineUnitPrice}
                              showOfflinePrices={showOfflinePrices}
                              showLimitedMyPrices={showLimitedMyPrices}
                              canViewPrices={canViewPrices}
                              expanded={expanded}
                              toggleExpanded={toggleExpanded}
                           />
                           {showRequestQuoteOption && (
                              <Box>
                                 <Link component="button" onClick={() => onRequestQuote()}>
                                    {translate('prices.quantity-new')}
                                 </Link>
                              </Box>
                           )}
                        </>
                     ) : productPriceCardItem.quotationLines?.length ? (
                        <Button variant="primary" trailingIcon={expanded ? 'chevron-up' : 'chevron-down'} fullWidth onClick={toggleExpanded}>
                           {translate('prices.quote-details')}
                        </Button>
                     ) : null}
                  </BreakpointSwitch>
               </Box>
            </BoxItem>
            <BreakpointSwitch excludeViews={['phone']}>
               <BoxItem>
                  <Box direction="column" items="end" height="100%">
                     {isOverview ? (
                        <>
                           <PriceSection
                              lowestUnitPrice={productPriceCardItem.lowestUnitPrice}
                              lowestOfflineUnitPrice={productPriceCardItem.lowestOfflineUnitPrice}
                              showOfflinePrices={showOfflinePrices}
                              showLimitedMyPrices={showLimitedMyPrices}
                              canViewPrices={canViewPrices}
                              expanded={expanded}
                              toggleExpanded={toggleExpanded}
                           />
                           {showRequestQuoteOption && (
                              <Link component="button" onClick={() => onRequestQuote()}>
                                 {translate('prices.quantity-new')}
                              </Link>
                           )}
                        </>
                     ) : productPriceCardItem.quotationLines?.length ? (
                        <Button variant="primary" trailingIcon={expanded ? 'chevron-up' : 'chevron-down'} onClick={toggleExpanded}>
                           {translate('prices.quote-details')}
                        </Button>
                     ) : null}
                  </Box>
               </BoxItem>
            </BreakpointSwitch>
         </Box>
         {productPriceCardItem.quotationLines && expanded && (
            <>
               <Divider />
               <Box direction="column">
                  {productPriceCardItem.quotationLines.map((line) => (
                     <BoxItem key={line.id} grow={1}>
                        <ProductPriceCardQuotationLine
                           packaging={productPriceCardItem.packaging}
                           quotationLine={line}
                           isOverview={isOverview}
                           quoteDetailBaseRoute={quoteDetailBaseRoute}
                           showShoppingCartBtn={showShoppingCartBtn}
                           hasOrderingPermission={hasOrderingPermission}
                           showOfflinePrices={showOfflinePrices}
                           isAddingToCart={cartContext.isAddingToCart}
                           onAddToCart={() => handleAddToCart(line)}
                        />
                     </BoxItem>
                  ))}
               </Box>
            </>
         )}
      </Paper>
   );
};
